import React, { useState } from 'react';
import { FaSnowflake, FaMapMarkerAlt, FaSearch, FaThermometerHalf, FaMountain, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaInfoCircle, FaCloudSun } from 'react-icons/fa';
import { getSkiConditions, geocodeLocation } from '../../api/client';

const SkiSnowVerifier = () => {
  const [loading, setLoading] = useState(false);
  const [snowData, setSnowData] = useState(null);
  const [error, setError] = useState('');

  // Search inputs
  const [searchCity, setSearchCity] = useState('');
  const [searchState, setSearchState] = useState('');
  const [searchCountry, setSearchCountry] = useState('');
  const [resortName, setResortName] = useState('');
  const [elevation, setElevation] = useState(2000);

  const handleSearch = async () => {
    if (!searchCity.trim()) {
      setError('Please enter a ski resort or mountain');
      return;
    }

    setLoading(true);
    setError('');
    setSnowData(null);

    try {
      // First geocode the location
      const geoData = await geocodeLocation(searchCity.trim(), searchState.trim() || null, searchCountry.trim() || null);

      if (!geoData.results || geoData.results.length === 0) {
        setError('Location not found. Try a different search term.');
        setLoading(false);
        return;
      }

      const location = geoData.results[0];
      const name = resortName.trim() || `${searchCity}${searchState ? ', ' + searchState : ''}`;

      // Get ski conditions
      const data = await getSkiConditions(location.lat, location.lng, name, elevation);
      setSnowData(data);
    } catch (err) {
      console.error('Error fetching ski conditions:', err);
      setError('Failed to fetch snow conditions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getRatingColor = (rating) => {
    switch (rating) {
      case 'Powder Alert!': return 'bg-blue-500';
      case 'Great': return 'bg-emerald-500';
      case 'Good': return 'bg-green-500';
      case 'Fair': return 'bg-yellow-500';
      default: return 'bg-red-500';
    }
  };

  const getQualityEmoji = (quality) => {
    switch (quality) {
      case 'Fresh Powder': return '❄️';
      case 'Powder': return '🌨️';
      case 'Packed Powder': return '⛷️';
      case 'Spring Snow': return '☀️';
      case 'Groomed': return '🎿';
      case 'Thin Cover': return '⚠️';
      default: return '❄️';
    }
  };

  return (
    <div className="space-y-6">
      {/* Hero Card */}
      <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 rounded-3xl p-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          {/* Snowflake animation */}
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute text-white/20 animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                fontSize: `${Math.random() * 20 + 10}px`,
                animationDelay: `${Math.random() * 3}s`
              }}
            >
              ❄
            </div>
          ))}
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
              <FaSnowflake className="text-2xl text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Ski Resort Snow Verifier</h2>
              <p className="text-white/70">Real snow conditions verification</p>
            </div>
          </div>

          <p className="text-white/80 mt-4">
            Check real snow conditions at ski resorts. Get accurate snow depth,
            recent snowfall data, and quality assessments before you hit the slopes.
          </p>
        </div>
      </div>

      {/* Search Card */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-neutral-100">
        <h3 className="text-lg font-semibold text-neutral-800 mb-4 flex items-center gap-2">
          <FaMapMarkerAlt className="text-blue-500" />
          Find Your Ski Resort
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Resort / Mountain *
            </label>
            <input
              type="text"
              value={searchCity}
              onChange={(e) => setSearchCity(e.target.value)}
              placeholder="e.g., Zermatt, Niseko"
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              State / Region
            </label>
            <input
              type="text"
              value={searchState}
              onChange={(e) => setSearchState(e.target.value)}
              placeholder="e.g., Valais, Hokkaido"
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Country
            </label>
            <input
              type="text"
              value={searchCountry}
              onChange={(e) => setSearchCountry(e.target.value)}
              placeholder="e.g., Switzerland, Japan"
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Resort Name (optional)
            </label>
            <input
              type="text"
              value={resortName}
              onChange={(e) => setResortName(e.target.value)}
              placeholder="e.g., Matterhorn Paradise"
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Base Elevation (m)
            </label>
            <select
              value={elevation}
              onChange={(e) => setElevation(parseInt(e.target.value))}
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            >
              <option value={1500}>1,500m (Low)</option>
              <option value={2000}>2,000m (Medium)</option>
              <option value={2500}>2,500m (High)</option>
              <option value={3000}>3,000m+ (Alpine)</option>
            </select>
          </div>
        </div>

        <button
          onClick={handleSearch}
          disabled={loading}
          className="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <FaSearch />
          )}
          {loading ? 'Checking Snow...' : 'Check Snow Conditions'}
        </button>

        {error && (
          <p className="text-red-600 text-sm mt-3">{error}</p>
        )}
      </div>

      {/* Results */}
      {snowData && (
        <div className="space-y-6 animate-fade-in">
          {/* Main Rating Card */}
          <div className={`${getRatingColor(snowData.snow_rating)} rounded-2xl p-6 text-white`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-4xl">{snowData.snow_rating === 'Powder Alert!' ? '🎉' : '⛷️'}</span>
                  <h3 className="text-2xl font-bold">{snowData.snow_rating}</h3>
                </div>
                <p className="text-white/90">{snowData.location_name}</p>
                <p className="text-white/70 text-sm">Elevation: {snowData.elevation_m}m</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold">{snowData.overall_score}/10</div>
                <p className="text-white/80 text-sm">Overall Score</p>
              </div>
            </div>
          </div>

          {/* Snow Depth Highlight */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-neutral-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center">
                <FaSnowflake className="text-4xl text-blue-400 mx-auto mb-2" />
                <p className="text-neutral-500 text-sm">Current Snow Depth</p>
                <div className="text-4xl font-bold text-blue-600">{snowData.snow_depth_cm} cm</div>
                <p className="text-neutral-400 text-sm">({snowData.snow_depth_inches} inches)</p>
              </div>
              <div className="text-center">
                <span className="text-4xl">{getQualityEmoji(snowData.snow_quality)}</span>
                <p className="text-neutral-500 text-sm mt-2">Snow Quality</p>
                <div className="text-2xl font-bold text-neutral-800">{snowData.snow_quality}</div>
                <p className="text-neutral-500 text-sm mt-1">{snowData.quality_details}</p>
              </div>
            </div>
          </div>

          {/* Fresh Snow Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-2xl p-5 text-center border border-blue-100">
              <p className="text-blue-600 text-sm font-medium">Last 24h</p>
              <div className="text-3xl font-bold text-blue-700">{snowData.fresh_snow_24h_cm} cm</div>
              <p className="text-blue-500 text-xs">Fresh snow</p>
            </div>
            <div className="bg-indigo-50 rounded-2xl p-5 text-center border border-indigo-100">
              <p className="text-indigo-600 text-sm font-medium">Last 48h</p>
              <div className="text-3xl font-bold text-indigo-700">{snowData.fresh_snow_48h_cm} cm</div>
              <p className="text-indigo-500 text-xs">Fresh snow</p>
            </div>
            <div className="bg-purple-50 rounded-2xl p-5 text-center border border-purple-100">
              <p className="text-purple-600 text-sm font-medium">Last 7 days</p>
              <div className="text-3xl font-bold text-purple-700">{snowData.fresh_snow_7d_cm} cm</div>
              <p className="text-purple-500 text-xs">Accumulated</p>
            </div>
          </div>

          {/* Weather & Forecast */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Current Weather */}
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5">
              <h4 className="font-semibold text-neutral-800 mb-4 flex items-center gap-2">
                <FaThermometerHalf className="text-blue-500" />
                Current Weather
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-neutral-500">Temperature</span>
                  <span className="font-bold text-lg">{snowData.temperature_c}°C ({snowData.temperature_f}°F)</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-neutral-500">Feels Like</span>
                  <span className="font-medium">{snowData.feels_like_c}°C</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-neutral-500">Wind</span>
                  <span className="font-medium">{snowData.wind_speed_kmh} km/h</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-neutral-500">Conditions</span>
                  <span className="font-medium">{snowData.weather_condition}</span>
                </div>
                {snowData.wind_chill_warning && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-red-700 text-sm flex items-center gap-2">
                    <FaExclamationTriangle /> Wind chill warning!
                  </div>
                )}
              </div>
            </div>

            {/* Forecast */}
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5">
              <h4 className="font-semibold text-neutral-800 mb-4 flex items-center gap-2">
                <FaCloudSun className="text-orange-500" />
                Snow Forecast
              </h4>
              <div className="space-y-3">
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                  <p className="text-sm text-blue-600 font-medium">Next 24 hours</p>
                  <p className="text-blue-900">{snowData.snow_forecast_24h}</p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                  <p className="text-sm text-indigo-600 font-medium">Next 48 hours</p>
                  <p className="text-indigo-900">{snowData.snow_forecast_48h}</p>
                </div>
                <div className="mt-2">
                  <p className="text-neutral-500 text-sm">Trend</p>
                  <p className="font-medium text-neutral-700">{snowData.temperature_trend}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Terrain Conditions */}
          <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5">
            <h4 className="font-semibold text-neutral-800 mb-4 flex items-center gap-2">
              <FaMountain className="text-emerald-500" />
              Terrain Conditions
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-neutral-50 rounded-xl p-4">
                <p className="text-sm text-neutral-500 mb-1">Groomed Runs</p>
                <p className="font-medium text-neutral-800">{snowData.groomed_runs}</p>
              </div>
              <div className="bg-neutral-50 rounded-xl p-4">
                <p className="text-sm text-neutral-500 mb-1">Off-Piste / Backcountry</p>
                <p className="font-medium text-neutral-800">{snowData.off_piste}</p>
              </div>
              <div className="bg-neutral-50 rounded-xl p-4">
                <p className="text-sm text-neutral-500 mb-1">Terrain Parks</p>
                <p className="font-medium text-neutral-800">{snowData.terrain_parks}</p>
              </div>
            </div>
          </div>

          {/* Recommendations */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* When to Go & Layers */}
            <div className="bg-blue-50 rounded-2xl p-5 border border-blue-100">
              <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <FaCheckCircle className="text-blue-500" />
                Recommendations
              </h4>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Best Time to Visit</p>
                  <p className="text-blue-900">{snowData.best_time_to_visit}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-600 font-medium">Layer Recommendation</p>
                  <p className="text-blue-900 text-sm">{snowData.layer_recommendation}</p>
                </div>
              </div>
            </div>

            {/* Gear */}
            <div className="bg-indigo-50 rounded-2xl p-5 border border-indigo-100">
              <h4 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                <FaSnowflake className="text-indigo-500" />
                Gear Checklist
              </h4>
              <ul className="space-y-1">
                {snowData.gear_suggestions.map((gear, idx) => (
                  <li key={idx} className="text-indigo-800 text-sm flex items-center gap-2">
                    <span className="text-indigo-500">✓</span>
                    {gear}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Warnings & Tips */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Warnings */}
            <div className="bg-amber-50 rounded-2xl p-5 border border-amber-100">
              <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                <FaExclamationTriangle className="text-amber-500" />
                Warnings
              </h4>
              <ul className="space-y-2">
                {snowData.warnings.map((warning, idx) => (
                  <li key={idx} className="text-amber-800 text-sm flex items-start gap-2">
                    <span className="text-amber-500 mt-1">⚠</span>
                    {warning}
                  </li>
                ))}
              </ul>
            </div>

            {/* Tips */}
            <div className="bg-emerald-50 rounded-2xl p-5 border border-emerald-100">
              <h4 className="font-semibold text-emerald-900 mb-3 flex items-center gap-2">
                <FaInfoCircle className="text-emerald-500" />
                Tips
              </h4>
              <ul className="space-y-2">
                {snowData.tips.map((tip, idx) => (
                  <li key={idx} className="text-emerald-800 text-sm flex items-start gap-2">
                    <span className="text-emerald-500 mt-1">•</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Should You Go? */}
          <div className={`${snowData.should_go ? 'bg-emerald-500' : 'bg-amber-500'} rounded-2xl p-6 text-white text-center`}>
            <div className="flex items-center justify-center gap-3 mb-2">
              {snowData.should_go ? (
                <FaCheckCircle className="text-3xl" />
              ) : (
                <FaTimesCircle className="text-3xl" />
              )}
              <h3 className="text-2xl font-bold">
                {snowData.should_go ? 'Hit the Slopes!' : 'Maybe Wait for Better Conditions'}
              </h3>
            </div>
            <p className="text-white/90">
              {snowData.should_go
                ? 'Conditions are favorable for a great day on the mountain!'
                : 'Current conditions may not be ideal. Check back for updates.'}
            </p>
          </div>
        </div>
      )}

      {/* Info Footer */}
      <div className="bg-blue-50 rounded-2xl p-6 flex items-start gap-4">
        <FaInfoCircle className="text-blue-500 text-xl flex-shrink-0 mt-1" />
        <div>
          <h4 className="font-semibold text-blue-900 mb-1">About Snow Data</h4>
          <p className="text-sm text-blue-700">
            Snow conditions are based on weather data and forecasts. Actual resort conditions
            may vary. Always check the resort's official snow report for the most accurate
            information about open lifts and runs. Remember to check avalanche conditions
            if venturing off-piste.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SkiSnowVerifier;
